import { z } from 'zod'
import type { CommandAnchor } from '../../lib/agent-command-anchor.js'
import { baseEntitySchema } from '../entity.js'
import { coreElementValueSchema } from '../slate/slate-element.js'

export const commentKinds = ['chat', 'nudge', 'proposal'] as const
export type CommentKind = (typeof commentKinds)[number]

export const commentSchema = baseEntitySchema.extend({
  type: z.literal('comment'),
  userId: z.string(),
  resolved: z.boolean().default(false).optional(),
  /**
   * What this comment IS: a chat turn, an agent nudge, or an agent proposal.
   * Optional (not defaulted) — legacy comments predate the field and are
   * recognized forever via the metadata-flag fallbacks in the guards below.
   * New writes stamp BOTH kind and the legacy flag during the deploy window.
   */
  kind: z.enum(commentKinds).describe('chat | nudge | proposal').optional(),
  children: coreElementValueSchema,
})

export type Comment = z.infer<typeof commentSchema>

export function isComment(node: unknown): node is Comment {
  return commentSchema.safeParse(node).success
}

const metadataFlag = (comment: Comment, flag: string): boolean =>
  typeof comment.metadata === 'object' &&
  comment.metadata !== null &&
  (comment.metadata as Record<string, unknown>)[flag] === true

export function isProposalComment(comment: Comment): boolean {
  return comment.kind === 'proposal' || metadataFlag(comment, 'agentProposal')
}

/**
 * Whether a row is the agent's words: written by 'assistant', or an agent proposal whoever owns the row.
 * Proposals written before GH #2451 are owned by the user who asked; reading the kind as well covers those
 * without migrating them. Takes a loose shape so chat items and raw store rows both fit.
 */
export const isAgentAuthoredComment = (row: { userId?: string; kind?: string; metadata?: unknown }): boolean =>
  row.userId === 'assistant' || isProposalComment(row as Comment)

export function isNudgeComment(comment: Comment): boolean {
  return comment.kind === 'nudge' || metadataFlag(comment, 'agentNudge')
}

export function isChatComment(comment: Comment): boolean {
  return !isProposalComment(comment) && !isNudgeComment(comment)
}

export interface EditorCommandProposalInput {
  /** The agent the proposal is a child of */
  agentId: string
  /** The invoking user, granted WRITE so they can accept or reject; authorship is `assistant` */
  userId: string
  /** The entity the command edits, e.g. the song */
  targetEntityId: string
  summary: string
  commandId: string
  args?: Record<string, unknown>
  anchor: CommandAnchor
}

/**
 * Who owns an agent proposal, and who may act on it (GH #2451). One source for every proposal the agent
 * server writes and for the E2E seed.
 *
 * - Owned by 'assistant', like the chat-reply Comment: the chat feed and the context assembler read
 *   authorship from `userId === 'assistant'`. The `create` call must pass 'assistant' too, because it
 *   stamps its own `userId` argument on the entity, whatever the partial says.
 * - The invoking user gets WRITE, not READ: accepting or rejecting stamps `metadata.resolution` with a
 *   client PATCH, which the graph update handler allows only with WRITE. That is no more than the user
 *   could do when these were (wrongly) owned by the user. The ACL principal is also how push delivers it.
 */
export const proposalOwnership = (invokingUserId: string) => ({
  userId: 'assistant',
  acls: [{ principal: invokingUserId, permission: 'WRITE' as const }],
})

/**
 * The partial entity an editor-command proposal is persisted as: what the agent server writes when a
 * tool proposes a command (`propose_chord_change`, `run_editor_command`), and what E2E seeds, so a
 * seeded proposal is the same thing a real agent turn produces (GH #2352).
 *
 * Ownership and access per `proposalOwnership`. `kind` and the legacy `metadata.agentProposal` flag are
 * both stamped for the deploy window. `anchorBlockId` lets the gutter chip find the proposal without
 * reading the patch.
 */
export const editorCommandProposalEntity = (input: EditorCommandProposalInput) => ({
  type: 'comment' as const,
  parentId: input.agentId,
  ...proposalOwnership(input.userId),
  kind: 'proposal' as const,
  children: [{ type: 'line' as const, children: [{ text: `[proposal] ${input.summary}` }] }],
  metadata: {
    agentProposal: true,
    targetEntityId: input.targetEntityId,
    summary: input.summary,
    anchorBlockId: input.anchor.id,
    blockSpan: 1,
    patch: {
      kind: 'editor-command' as const,
      commandId: input.commandId,
      args: input.args,
      anchor: input.anchor,
    },
  },
})
